import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileClock, Plus, Search } from 'lucide-react';
import { api, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate } from '@/lib/dates';
import { PageHeader } from '@/app/DashboardLayout';
import type { ProformaStatus } from '@fint/validation';
import {
  Button, Card, EmptyState, ErrorState, Input, Money, ProformaStatusBadge, SkeletonTable,
  TBody, TD, TH, THead, TR, Table,
} from '@/features/common/ui';

interface ProformaRow {
  id: string;
  number: string;
  customerName: string;
  date: string;
  expiryDate: string | null;
  currency: string;
  total: string;
  status: ProformaStatus;
  convertedToInvoiceNumber: string | null;
}

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'open', label: 'Awaiting a decision' },
  { value: 'converted', label: 'Converted' },
] as const;

export function ProformasPage() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? 'all';
  const [search, setSearch] = useState(searchParams.get('q') ?? '');

  const query = useQuery({
    queryKey: ['proformas', org?.id, status, search],
    queryFn: () => api.get<{ items: ProformaRow[] }>(`/proformas${qs({ status, q: search })}`),
    enabled: !!org?.id,
  });

  const setStatus = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('status');
    else params.set('status', next);
    setSearchParams(params, { replace: true });
  };

  return (
    <>
      <PageHeader
        title="Proformas"
        // Says what the document is, on the screen a user reaches it from.
        // "Proforma" is a word plenty of small businesses have only half heard.
        subtitle="Ask to be paid before you supply. Not a tax invoice, and not money you are owed."
        actions={
          perms.can('proforma', 'create') ? (
            <Button asChild size="sm">
              <Link to="/proformas/new">
                <Plus className="size-4" aria-hidden />
                New proforma
              </Link>
            </Button>
          ) : null
        }
      />

      <div className="space-y-4 px-5 pb-8">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1 max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              className="pl-8"
              placeholder="Search by number, reference or customer"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search proformas"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => setStatus(filter.value)}
                aria-pressed={status === filter.value}
                className={
                  status === filter.value
                    ? 'rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary'
                    : 'rounded-full px-3 py-1 text-xs text-muted-foreground hover:bg-accent'
                }
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <Card>
          {query.isError ? (
            <ErrorState error={query.error} onRetry={() => query.refetch()} />
          ) : query.isLoading ? (
            <SkeletonTable rows={6} cols={5} />
          ) : query.data?.items.length ? (
            <Table>
              <THead>
                <TR>
                  <TH>Number</TH>
                  <TH>Customer</TH>
                  <TH>Date</TH>
                  <TH>Valid until</TH>
                  <TH>Status</TH>
                  {/* No Balance column, and its absence is the point: a proforma
                      is never money owed, so a column of zeroes would suggest
                      it could be. */}
                  <TH numeric>Total</TH>
                </TR>
              </THead>
              <TBody>
                {query.data.items.map((proforma) => (
                  <TR key={proforma.id}>
                    <TD>
                      <Link to={`/proformas/${proforma.id}`} className="font-medium text-primary hover:underline">
                        {proforma.number}
                      </Link>
                    </TD>
                    <TD>{proforma.customerName}</TD>
                    <TD className="text-muted-foreground">{formatDate(proforma.date)}</TD>
                    <TD className="text-muted-foreground">{formatDate(proforma.expiryDate)}</TD>
                    <TD>
                      <div className="flex items-center gap-2">
                        <ProformaStatusBadge status={proforma.status} />
                        {proforma.convertedToInvoiceNumber ? (
                          <span className="text-xs text-muted-foreground">{proforma.convertedToInvoiceNumber}</span>
                        ) : null}
                      </div>
                    </TD>
                    <TD numeric>
                      <Money value={proforma.total} currency={proforma.currency} />
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          ) : (
            <EmptyState
              icon={FileClock}
              title={status === 'all' && !search ? 'No proformas yet' : 'Nothing matches that'}
              description={
                status === 'all' && !search
                  ? 'A proforma asks a customer to pay before you supply. When they agree, it becomes a real invoice in one click.'
                  : 'Try a different filter or search term.'
              }
              action={
                status === 'all' && !search && perms.can('proforma', 'create') ? (
                  <Button asChild size="sm">
                    <Link to="/proformas/new">New proforma</Link>
                  </Button>
                ) : null
              }
            />
          )}
        </Card>
      </div>
    </>
  );
}
